import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import { useEffect, useState } from 'react'
import { FormGroup, FormLabel } from 'react-bootstrap-five'
import FormsyDatetimePicker from 'scenes/CampaignCenter/components/FormsyDatetimePicker'
import 'utilities/dateRangeValidations'

type Props = {
  blockId: string
  showStartDate: boolean
  showEndDate: boolean
  startAt?: string
  endAt?: string
  updateContentBlock: (blockId: string, data: any) => void
}

const t = buildTranslate(
  'cms.content.app_home_page.content.edit_block.configuration'
)

const DateRangeSection = ({
  blockId,
  showStartDate: initialShowStartDate,
  showEndDate: initialShowEndDate,
  startAt,
  endAt,
  updateContentBlock,
}: Props) => {
  const [showStartDate, setShowStartDate] = useState(initialShowStartDate)
  const [showEndDate, setShowEndDate] = useState(initialShowEndDate)

  useEffect(() => {
    // Only sync with props if they become true (indicating data exists)
    // Don't reset to false when props become false, as user might have manually shown the field
    if (initialShowStartDate) {
      setShowStartDate(initialShowStartDate)
    }
    if (initialShowEndDate) {
      setShowEndDate(initialShowEndDate)
    }
  }, [initialShowStartDate, initialShowEndDate])

  const renderDateSection = (
    type: 'start' | 'end',
    showDate: boolean,
    setShowDate: (show: boolean) => void,
    labelKey: string,
    addButtonKey: string,
    datetimeValue?: string
  ) => {
    // Use the datetime value directly - it's already in ISO format
    const combinedDateTime = datetimeValue || null

    return (
      <FormGroup className="mb-l">
        <FormLabel className="body-text-4 bold mb-xs">{t(labelKey)}</FormLabel>

        <Switch condition={!showDate}>
          <div>
            <button
              type="button"
              className="bg-transparent border-0 p-0 text-decoration-none"
              onClick={() => setShowDate(true)}
            >
              <Text.BodyText4 color="primary" className="bold">
                {t(addButtonKey)}
              </Text.BodyText4>
            </button>
          </div>
        </Switch>

        <Switch condition={showDate}>
          <>
            <FormsyDatetimePicker
              name={`${blockId}.${type}_at`}
              value={combinedDateTime}
              isNullable={false}
              calendarPlacement="top"
              startEmpty={true}
              validations={
                type === 'start'
                  ? {
                      datetimeRequired: true,
                      startDatetimeBeforeEnd: { blockId },
                      startDatetimeNotInPast: true,
                    }
                  : {
                      datetimeRequired: true,
                      endDatetimeNotInPast: true,
                    }
              }
            />
            <div className="mt-s">
              <button
                type="button"
                className="bg-transparent border-0 p-0 text-decoration-none"
                onClick={() => {
                  setShowDate(false)
                  // Clear the corresponding datetime field
                  const fieldToClear = type === 'start' ? 'start_at' : 'end_at'
                  updateContentBlock(blockId, { [fieldToClear]: null })
                }}
              >
                <Text.BodyText4 color="primary" className="bold">
                  {t('content_display.date_range.remove')}
                </Text.BodyText4>
              </button>
            </div>
          </>
        </Switch>
      </FormGroup>
    )
  }

  return (
    <div className="pl-l">
      {renderDateSection(
        'start',
        showStartDate,
        setShowStartDate,
        'content_display.date_range.start_label_optional',
        'content_display.date_range.add_start_date',
        startAt
      )}

      {renderDateSection(
        'end',
        showEndDate,
        setShowEndDate,
        'content_display.date_range.end_label_optional',
        'content_display.date_range.add_end_date',
        endAt
      )}
    </div>
  )
}

export default DateRangeSection
